import * as logger from "../utils/logger";
import { COACHING_SESSION_STATUS, COACHING_SYNC_LOG_PREFIX, NEW_SESSION_KEY } from "../modules/coaching/constants";
import { CoachingActions } from "../modules/coaching/store/actions";
import { CoachingCommentSync } from "./coachingCommentSync";
import { CoachingCompletionSync } from "./coachingCompletionSync";
import { CoachingNotificationService } from "../modules/coaching/services/coachingNotificationService";
import { CoachingSessionService } from "../modules/coaching/services/coachingSessionService";
import { DriveStore } from "../store/reducers";
import { ICoachingComment, ICoachingPendingCompletion } from "../modules/coaching/store/types";
import { IApiError } from "../utils/api.interfaces";
import { NumberOfSyncRetries } from "./base";
import { isDuplicateException, isInvalidPermissionsException } from "../utils/errorHandler/error.types";
import { isApiError, isTerminalError } from "./coachingSyncErrors";
import { isPendingComment, isPendingCompletion } from "./coachingSyncFilters";

/** Stage tag appended to the shared root so the whole flow greps by `[CoachingSync]`. */
const LOG_PREFIX = `${ COACHING_SYNC_LOG_PREFIX } [NewSession]`;

/**
 * Resolves the driver's NEW (not-yet-created) coaching session so its offline comments and
 * completion can sync under a real id. The orchestrator (CoachingSync) runs this first each
 * cycle; this class owns the entire `"new"` lifecycle on a bounded retry budget
 * (`newSessionResolution` in the store):
 *   - resolved        → re-key the `"new"` buckets onto the real id, clear the op
 *   - obsolete        → the driver has no open coaching session anymore: drop the work, notify
 *   - terminal        → drop the work (notify on a permission error), clear the op
 *   - transient/other → decrement the budget; drop only once it is exhausted
 *   - non-API fault   → drop the work, clear the op, and surface the exception
 * The comment/completion drops reuse CoachingCommentSync / CoachingCompletionSync so the failed
 * state is written exactly as it is on the normal push path.
 */
export class CoachingNewSessionSync {
    constructor(
        private readonly store: DriveStore,
        private readonly sessionService: CoachingSessionService,
        private readonly notifications: CoachingNotificationService,
        private readonly commentSync: CoachingCommentSync,
        private readonly completionSync: CoachingCompletionSync
    ) {}

    async resolve(userId: string): Promise<void> {
        const state = this.store.getState().coaching?.[userId];
        const capturedComments = (state?.comments?.[NEW_SESSION_KEY] ?? [])
            .filter(isPendingComment)
            .slice()
            .sort((a, b) => (a.dateTime < b.dateTime ? -1 : a.dateTime > b.dateTime ? 1 : 0));
        const rawCompletion = state?.pendingCompletions?.[NEW_SESSION_KEY];
        const pendingCompletion = rawCompletion && isPendingCompletion(rawCompletion) ? rawCompletion : undefined;

        const pendingCommentCount = capturedComments.length;
        const hasPendingCompletion = !!pendingCompletion;
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } resolve entry: ${ pendingCommentCount } pending comments, completion=${ hasPendingCompletion }`,
            userId
        );

        if (!capturedComments.length && !pendingCompletion) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } no pending NEW work, clearing dangling op`,
                userId
            );
            this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
            return;
        }

        let resolvedId: string | undefined;
        try {
            resolvedId = await this.resolveSessionId(userId);
            const resolvedIdDisplay = resolvedId !== undefined ? resolvedId : "undefined";
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } resolved session id: ${ resolvedIdDisplay }`,
                userId
            );
        } catch (error) {
            if (!isApiError(error)) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } non-API error during resolution: ${ errorMessage }, failing work and surfacing exception`,
                    userId
                );
                this.dropPendingWork(userId, capturedComments, pendingCompletion, "syncFailed");
                this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
                throw error;
            }
            if (isInvalidPermissionsException(error)) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } permission error, dropping work and notifying driver`,
                    userId
                );
                this.dropPendingWork(userId, capturedComments, pendingCompletion, "syncFailed");
                if (capturedComments.length > 0) {
                    this.notifications.notifyCommentDroppedDueToNoPermission(userId, NEW_SESSION_KEY);
                }
                if (pendingCompletion) {
                    this.notifications.notifySessionCompletionDroppedDueToNoPermission(userId, NEW_SESSION_KEY);
                }
                this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
                return;
            }
            if (isTerminalError(error)) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } terminal error, dropping work`,
                    userId
                );
                this.dropPendingWork(userId, capturedComments, pendingCompletion, "syncFailed");
                this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
                return;
            }
            // Default path: transient/unclassified — retry on a bounded budget, fail only when spent.
            const remaining = (state?.newSessionResolution?._syncCounter ?? NumberOfSyncRetries) - 1;
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } transient error, remaining retry budget: ${ remaining }`,
                userId
            );
            if (remaining <= 0) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } retry budget exhausted, dropping work`,
                    userId
                );
                this.dropPendingWork(userId, capturedComments, pendingCompletion, "syncFailed");
                this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
            } else {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } decrementing retry budget, will retry next cycle`,
                    userId
                );
                this.store.dispatch(
                    CoachingActions.setNewSessionResolution({ userId, resolution: { _syncCounter: remaining } })
                );
            }
            return;
        }

        if (!resolvedId) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } driver has no open session, work is obsolete, looking up completed session to re-key`,
                userId
            );

            // The user's NEW session was completed by another party before we could sync.
            // Try to find the completed session so we can re-key the pending work to its
            // real ID; otherwise failure markers under the "new" sentinel leak onto future
            // sessions when the driver gets a new coaching assignment.
            let completedSessionId: string | undefined;
            try {
                const allSessions = await this.sessionService.getCoachingSessions();
                const completed = allSessions
                    .filter((s) => s.status === COACHING_SESSION_STATUS.COMPLETED)
                    .sort((a, b) => {
                        const aDate = a.acknowledgementDateTime ?? a.modifiedDate ?? "";
                        const bDate = b.acknowledgementDateTime ?? b.modifiedDate ?? "";
                        return bDate.localeCompare(aDate);
                    })[0];
                completedSessionId = completed?.id;
            } catch {
                // Fetch failed — fall through without re-keying.
            }

            if (completedSessionId) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } re-keying obsolete NEW work to completed session ${ completedSessionId }`,
                    userId
                );
                this.rekey(userId, completedSessionId, !!pendingCompletion);
                // Clear the resolution op; the orchestrator picks up the re-keyed work
                // in the same cycle and flushSession handles the completed-session drop
                // with the correct session id (notifications included).
            } else {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } no completed session found, discarding pending items from new key`,
                    userId
                );
                this.store.dispatch(
                    CoachingActions.removePendingSessionComments({ userId, sessionId: NEW_SESSION_KEY })
                );
                this.store.dispatch(
                    CoachingActions.removePendingSessionCompletion({ userId, sessionId: NEW_SESSION_KEY })
                );
            }

            this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
            return;
        }

        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } re-keying NEW session to real id ${ resolvedId }, clearing op`,
            userId
        );
        this.rekey(userId, resolvedId, !!pendingCompletion);
        this.store.dispatch(CoachingActions.setNewSessionResolution({ userId }));
    }

    /** Fail the NEW session's queued comments and completion (it can't be resolved). */
    private dropPendingWork(
        userId: string,
        comments: ICoachingComment[],
        completion: ICoachingPendingCompletion | undefined,
        reason: string
    ): void {
        if (comments.length > 0) {
            this.commentSync.drop(userId, NEW_SESSION_KEY, comments, reason);
        }
        if (completion) {
            this.completionSync.drop(userId, NEW_SESSION_KEY, reason);
        }
    }

    /**
     * Resolve the NEW-session key to a real session id: reuse the driver's Active session if one
     * exists, otherwise create it from the still-open virtual New session. Returns undefined when
     * the driver has no open coaching session anymore (Active nor New) — the queued work is then
     * obsolete and the caller drops it.
     */
    private async resolveSessionId(userId: string): Promise<string | undefined> {
        const openSessions = await this.sessionService.getOpenSessions(userId);
        const active = openSessions.find((s) => s.state === COACHING_SESSION_STATUS.ACTIVE);
        if (active) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } reusing existing Active session ${ active.id }`,
                userId
            );
            return active.id;
        }
        if (!openSessions.some((s) => s.state === COACHING_SESSION_STATUS.NEW)) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } no Active and no virtual New session, returning undefined (obsolete)`,
                userId
            );
            return undefined;
        }
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } creating new session from virtual New`,
            userId
        );
        try {
            return await this.sessionService.createCoachingSession(userId);
        } catch (createError) {
            if (!isDuplicateException(createError as IApiError)) {
                throw createError;
            }
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } DuplicateException race detected, reusing Active session produced by race`,
                userId
            );
            const retryActive = (await this.sessionService.getOpenSessions(userId))
                .find((s) => s.state === COACHING_SESSION_STATUS.ACTIVE);
            if (!retryActive) {
                throw createError;
            }
            return retryActive.id;
        }
    }

    /** Move a resolved NEW session's pending comments and completion onto the real session id. */
    private rekey(userId: string, sessionId: string, hasPendingCompletion: boolean): void {
        this.store.dispatch(
            CoachingActions.reassignPendingComments({
                userId,
                fromSessionId: NEW_SESSION_KEY,
                toSessionId: sessionId
            })
        );
        if (hasPendingCompletion) {
            this.store.dispatch(
                CoachingActions.updateCoachingCompletionState({
                    userId,
                    sessionId: NEW_SESSION_KEY,
                    patch: { sessionId }
                })
            );
        }
    }
}
